import {
  type CatalogLookup,
  type CustomCatalog,
} from '../../../utils/customCatalogs';

export interface CatalogPickerOption {
  key: string;
  label: string;
}

/** The lookup answered, and the catalog is not there. */
export const MISSING_CATALOG_LABEL = 'Catalog no longer exists';

/** The lookup did not answer, so whether the catalog exists is unknown. */
export const UNAVAILABLE_CATALOG_LABEL = "Couldn't load catalog";

interface ResolveSelectedLabelParams {
  value: string;
  fixedOptions: CatalogPickerOption[];
  /** The selected catalog looked up by id; `null` for a fixed option. */
  lookup: CatalogLookup | null;
}

/**
 * What the box reads when nothing is being typed. Empty while the lookup is in
 * flight, so a catalog is never briefly announced as gone or broken.
 */
export const resolveSelectedLabel = ({
  value,
  fixedOptions,
  lookup,
}: ResolveSelectedLabelParams): string => {
  const fixed = fixedOptions.find((option) => option.key === value);
  if (fixed) return fixed.label;
  if (!value || !lookup) return '';

  switch (lookup.status) {
    case 'found':
      return lookup.catalog.name;
    case 'not-found':
      return MISSING_CATALOG_LABEL;
    case 'failed':
      return UNAVAILABLE_CATALOG_LABEL;
    case 'loading':
      return '';
    default: {
      const exhaustive: never = lookup;
      return exhaustive;
    }
  }
};

export const filterFixedOptions = (
  options: CatalogPickerOption[],
  query: string
): CatalogPickerOption[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;

  return options.filter(({ label }) => label.toLowerCase().includes(needle));
};

interface BuildPickerOptionsParams {
  fixedOptions: CatalogPickerOption[];
  catalogs: CustomCatalog[];
  selectedKey: string;
  selectedLabel: string;
}

/**
 * The fixed options, then the catalogs the search returned, with the selected
 * one appended when it is not among them — HeroUI renders no selection at all
 * for a key it has no item for, which is what showed a saved catalog as blank.
 */
export const buildPickerOptions = ({
  fixedOptions,
  catalogs,
  selectedKey,
  selectedLabel,
}: BuildPickerOptionsParams): CatalogPickerOption[] => {
  const options: CatalogPickerOption[] = [
    ...fixedOptions,
    ...catalogs.map(({ id, name }) => ({ key: id, label: name })),
  ];

  if (!selectedKey || !selectedLabel) return options;
  if (options.some((option) => option.key === selectedKey)) return options;

  return [...options, { key: selectedKey, label: selectedLabel }];
};
